'use client';

import { useEffect, useRef } from 'react';
import { toast } from 'sonner';

import { api } from '@/lib/api';
import {
  isLaunchTerminal,
  isOwnLaunch,
  launchToastId,
  markLaunchTerminal,
} from '@/lib/launch-tracking';
import { LAUNCH_TIMEOUT_MESSAGE } from '@/lib/launch-request';
import type { LaunchEventPayload, LaunchPhase } from '@/hooks/useWebSocket';

/**
 * Following a dock launch from the press to the aircraft (#418).
 *
 * WHY THIS EXISTS. A docked launch used to be one HTTP request: the operator pressed LAUNCH,
 * the card sat busy for up to a minute, and the response said `dispatched`. The door now answers
 * `202 accepted` as soon as the bay is confirmed open, and everything that matters after that -
 * the aircraft acquiring a home point on the open tray, then the mission actually being
 * commanded - happens with nobody holding the request. Without this the operator presses LAUNCH,
 * the card closes, and the next thing they learn is whether an aircraft took off.
 *
 * THE TOAST IS KEYED BY LAUNCH ID, so the card that opens it and this hook that resolves it need
 * know nothing about each other. `DockLaunchOverlay` opens a loading toast at `launch:<id>` on
 * the 202 and closes; frames arriving here land on that same toast.
 *
 * ONLY THIS BROWSER'S LAUNCHES - see `launch-tracking.ts` for why an org-wide toast is a false
 * alarm rather than a shared air picture.
 */

/** How often the hold's countdown may repaint the toast. */
const HOLD_REPAINT_MS = 5_000;

/**
 * How long a launch may go unresolved before this hook stops waiting and says so.
 *
 * A LOADING TOAST IS A TRAP WITHOUT THIS. Sonner never auto-dismisses a `loading` toast and
 * never renders a close button on one, so an orphaned launch toast is pinned to the corner of
 * every page, undismissable, until a full reload. Four ways to get one, all reachable: the
 * operator navigates away (the socket is per-page, so the subscription dies with it), the socket
 * drops across the terminal frame, a Redis write fails so no frame is ever sent, or the API
 * restarts mid-sequence.
 *
 * Sized above everything the server may legitimately spend: the bay (~50 s) plus
 * `HOME_POINT_WAIT_ASYNC_MS` (180 s) plus the dispatch, with margin.
 */
const LAUNCH_FOLLOW_TIMEOUT_MS = 300_000;

/** The one line an operator reads, with the airframe it is about. */
function phaseCopy(
  event: LaunchEventPayload,
  callsign: string | null,
): { text: string; kind: 'loading' | 'success' | 'error' } | null {
  // LEADS WITH THE CALLSIGN, per the military-UI convention. Two docks launching inside the
  // same minute otherwise produce two identical anonymous spinners.
  const who = callsign ? `${callsign.toUpperCase()} - ` : '';
  const phase: LaunchPhase = event.phase;
  switch (phase) {
    case 'accepted':
      // The card that pressed LAUNCH is still on screen and already says this.
      return null;
    case 'bay_opening':
      return { text: `${who}opening the bay`, kind: 'loading' };
    case 'awaiting_home_point':
      // The line #418 asked for. `detail` carries the holding time, rebuilt by the server on
      // every poll, so this reads as live rather than frozen.
      return {
        text: `${who}${event.detail ?? 'BAY OPEN - waiting for the aircraft to record a home point'}`,
        kind: 'loading',
      };
    case 'dispatching':
      return { text: `${who}commanding the aircraft`, kind: 'loading' };
    case 'dispatched':
      return { text: `${who}launch dispatched`, kind: 'success' };
    case 'refused':
      return { text: `${who}${event.detail ?? 'launch refused'}`, kind: 'error' };
    default:
      // An unrecognised phase from a newer server. Say nothing rather than render a raw enum.
      return null;
  }
}

export function useCopLaunchProgress(
  subscribeLaunchEvent: (cb: (event: LaunchEventPayload) => void) => () => void,
  droneMap: ReadonlyMap<string, { callsign?: string | null }>,
): void {
  // Read through a ref so a repainting drone map does not tear down the subscription, which
  // would drop frames for a launch already in the air. Synced in an effect rather than during
  // render: a ref write during render is not a render-safe read for React's concurrent modes.
  const droneMapRef = useRef(droneMap);
  useEffect(() => {
    droneMapRef.current = droneMap;
  }, [droneMap]);

  /** Last repaint per launch, so a 1 Hz hold does not update the toast 180 times. */
  const lastPaintRef = useRef(new Map<string, number>());
  /** Give-up timers, one per followed launch. */
  const timersRef = useRef(new Map<string, ReturnType<typeof setTimeout>>());

  useEffect(() => {
    const timers = timersRef.current;

    const settle = (launchId: string, run: () => void): void => {
      markLaunchTerminal(launchId);
      const timer = timers.get(launchId);
      if (timer) {
        clearTimeout(timer);
        timers.delete(launchId);
      }
      lastPaintRef.current.delete(launchId);
      run();
    };

    /**
     * Nothing has been heard about this launch for long enough. Ask the server once, because
     * the whole reason `GET /api/launches/:launchId` exists is this case, and only fall back to
     * INDETERMINATE if that cannot answer either.
     */
    const giveUp = (launchId: string): void => {
      void (async () => {
        const id = launchToastId(launchId);
        try {
          const res = await api.get<{ data?: LaunchEventPayload }>(`/api/launches/${launchId}`);
          const record = res?.data;
          if (record && (record.phase === 'dispatched' || record.phase === 'refused')) {
            settle(launchId, () => {
              if (record.phase === 'dispatched') {
                toast.success('Launch dispatched', { id });
              } else {
                toast.error(record.detail ?? 'Launch refused', { id, duration: 15_000 });
              }
            });
            return;
          }
        } catch {
          // Fall through: an unreachable or expired record is the same answer as no answer.
        }
        // The repo's existing doctrine for a launch whose outcome cannot be established: the
        // copy sends the operator to VERIFY, never to blind-retry.
        settle(launchId, () => {
          toast.warning(LAUNCH_TIMEOUT_MESSAGE, { id, duration: 20_000 });
        });
      })();
    };

    const unsubscribe = subscribeLaunchEvent((event) => {
      // Not this browser's launch, or one already finished. See `launch-tracking.ts`.
      if (!isOwnLaunch(event.launch_id) || isLaunchTerminal(event.launch_id)) return;

      const callsign = droneMapRef.current.get(event.drone_id)?.callsign ?? null;
      const copy = phaseCopy(event, callsign);
      if (!copy) return;

      const id = launchToastId(event.launch_id);
      const isTerminal = copy.kind !== 'loading';

      // Arm the give-up timer on the first frame that actually holds, and let each new frame
      // push it out - a launch that is still reporting is not stuck.
      if (!isTerminal) {
        const existing = timers.get(event.launch_id);
        if (existing) clearTimeout(existing);
        timers.set(event.launch_id, setTimeout(() => giveUp(event.launch_id), LAUNCH_FOLLOW_TIMEOUT_MS));

        // THROTTLED. `awaiting_home_point` arrives at 1 Hz for up to three minutes, and Sonner's
        // region is `aria-live="polite" aria-relevant="additions text"` - so every repaint is
        // re-announced, which would saturate a screen reader's queue and suppress every other
        // announcement in the app, alerts included. Five seconds is plenty for a human reading a
        // holding time.
        const last = lastPaintRef.current.get(event.launch_id) ?? 0;
        if (Date.now() - last < HOLD_REPAINT_MS) return;
        lastPaintRef.current.set(event.launch_id, Date.now());

        toast.loading(copy.text, { id });
        return;
      }

      // ADVISORIES ARE A CAUTION, NEVER GREEN SMALL PRINT. Under `richColors` a success toast
      // prints its description in the colour this product reserves for "all good", and its
      // default life is 4 s - so an airspace conflict or the #469 station-altitude mismatch
      // would be typeset as reassurance and gone before it was read. This is now the ONLY
      // delivery for those notes: the `accepted` body carries none.
      const description = event.advisories.length ? event.advisories.join(' · ') : undefined;
      settle(event.launch_id, () => {
        if (copy.kind === 'error') {
          toast.error(copy.text, { id, description, duration: 15_000 });
        } else if (description) {
          toast.warning(copy.text, { id, description, duration: 10_000 });
        } else {
          toast.success(copy.text, { id });
        }
      });
    });

    return () => {
      unsubscribe();
      // Never leave a timer pointing at an unmounted subscriber.
      timers.forEach((t) => clearTimeout(t));
      timers.clear();
    };
  }, [subscribeLaunchEvent]);
}
